import React, {useEffect, useState} from 'react';
import articleService from "../../../services/articles.service";
import {useNavigate} from 'react-router-dom';
import {Alert, Button, Collapse, Pagination, Table, Form, InputGroup} from 'react-bootstrap';
import Loading from "../../common/Loading";

function ArticlesDashboard() {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0
    });

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getArticles = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Clear articles when filters change (but not on pagination changes)
                if (searchTerm || selectedCategory) {
                    setArticles([]);
                }

                // First fetch categories if not already loaded
                if (categories.length === 0) {
                    const categoriesRes = await articleService.getArticlesCategory({signal: controller.signal});
                    if (isMounted) {
                        setCategories(categoriesRes.data);
                    }
                }

                // Then fetch articles with filters
                const res = await articleService.getArticles(
                    pagination.page,
                    pagination.size,
                    selectedCategory,
                    searchTerm,
                    {signal: controller.signal}
                );

                if (isMounted) {
                    setArticles(res.data.content);
                    setPagination(prev => ({
                        ...prev,
                        totalElements: res.data.totalElements,
                        totalPages: res.data.totalPages
                    }));

                    // Set error if no results found with active filters
                    if (res.data.content.length === 0 && (searchTerm || selectedCategory)) {
                        setError('No articles found matching your filters.');
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error("Fetch error:", err);
                    setError('No articles found matching your filters');
                    setArticles([]); // Clear articles on error
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getArticles();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [pagination.page, pagination.size, selectedCategory, searchTerm]);

    const handleSearch = (e) => {
        e.preventDefault();
        // Reset to first page when searching
        setPagination(prev => ({...prev, page: 0}));
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setPagination(prev => ({...prev, page: 0}));
    };

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    const handleEditClick = (id) => {
        navigate(`/articles/${id}`);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({...prev, page: page - 1}));
    };

    const handleSizeChange = (e) => {
        setPagination(prev => ({...prev, size: Number(e.target.value), page: 0}));
    };

    const truncateSummary = (text, length = 300) => {
        if (!text) return '';
        return text.length > length
            ? `${text.substring(0, length)}...`
            : text;
    };

    return (
        <div className="container-fluid py-4">
            <div className="card card-padding shadow-sm">
                <div className="break"></div>
                <div className="card-body">
                    {/* Search Filters Section */}
                    <div className="mb-4 p-3 rounded">
                        <form onSubmit={handleSearch}>
                            <div className="row g-3 align-items-end">
                                <div className="col-md-6">
                                    <label>Search by Title</label>
                                    <div className="input-group input-group-sm">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter article title..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-secondary"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label>Filter by Category</label>
                                    <div className="input-group-sm">
                                    <select
                                        className="form-control"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        <option value="">All Categories</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.title}
                                            </option>
                                        ))}
                                    </select>
                                    </div>
                                </div>

                                <div className="col-md-2 d-flex gap-2">
                                    <button className="btn btn-primary btn-sm" type="submit">
                                        <i className="bi bi-search me-1"></i> Search
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={handleClearFilters}
                                        disabled={!searchTerm && !selectedCategory}
                                    >
                                        <i className="bi bi-x"></i> Clear
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>


                    {isLoading ? (
                        <Loading/>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table striped bordered hover className="mb-0">
                                    <thead className="table-light">
                                    <tr>
                                        <th width="35%"><i className="bi bi-file-medical"></i> Article Title</th>
                                        <th width="25%"><i className="bi bi-tag"></i> Category</th>
                                        <th width="10%"><i className="bi bi-check-circle text-success"></i> Status</th>
                                        <th width="18%"><i className="bi bi-arrow-clockwise"></i> Last Updated</th>
                                        <th width="12%"><i className="bi bi-gear"></i> Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {articles.length > 0 ? (
                                        articles.map(article => (
                                            <React.Fragment key={article.id}>
                                                <tr>
                                                    <td>
                                                        <div>{article.title}</div>
                                                    </td>
                                                    <td>
                                                <span className="badge bg-primary bg-opacity-10 text-primary">
                                                  {article.category}
                                                </span>
                                                    </td>
                                                    <td>
                                                        {article.status ? (
                                                            <div
                                                                className="badge status-completed text-success px-2 py-1">
                                                                <i className="bi bi-toggle-on text-success"></i> Active
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="badge status-incomplete text-danger px-2 py-1">
                                                                <i className="bi bi-toggle-off text-danger"></i> Disable
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="justify-content-start align-items-center">
                                                        <div className="small"> {article.createdAt}</div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => handleEditClick(article.id)}
                                                                title="Edit"
                                                            >
                                                                <i className="bi bi-pencil-square"></i> Edit
                                                            </button>
                                                            <button

                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => toggleRow(article.id)}
                                                                title="Toggle Summary"
                                                                aria-expanded={expandedRows.includes(article.id)}
                                                            >
                                                                {expandedRows.includes(article.id) ? (
                                                                    <i className="bi bi-chevron-up"></i>
                                                                ) : (
                                                                    <i className="bi bi-chevron-down"></i>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="5" className="p-0 border-top-0">
                                                        <Collapse in={expandedRows.includes(article.id)}>
                                                            <div className="p-3 bg-light">
                                                                <small className="text-muted">ID: {article.id}</small>
                                                                <hr/>
                                                                <div className="fw-semibold mb-2">Summary</div>
                                                                <p className="mb-0">
                                                                    {expandedRows.includes(article.id)
                                                                        ? article.summary
                                                                        : truncateSummary(article.summary)}
                                                                </p>
                                                                {article.summary && article.summary.length > 300 && (
                                                                    <Button
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="ps-0 mt-2"
                                                                        onClick={() => toggleRow(article.id)}
                                                                    >
                                                                        {expandedRows.includes(article.id)
                                                                            ? 'Show less'
                                                                            : 'Show more'}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                <div className="alert alert-danger">
                                                    {error ? error :  "Error: No Articles found!!"}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </Table>
                            </div>

                            {articles.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2">Show:</span>
                                        <select
                                            className="form-select form-select-sm w-auto"
                                            value={pagination.size}
                                            onChange={handleSizeChange}
                                        >
                                            {[5, 10, 20, 50].map(size => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                        <span className="ms-2 text-muted">
                      {pagination.totalElements} total articles
                    </span>
                                    </div>

                                    <Pagination>
                                        <Pagination.First
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(1)}
                                        />
                                        <Pagination.Prev
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(pagination.page)}
                                        />

                                        {Array.from({length: Math.min(5, pagination.totalPages)}, (_, i) => {
                                            const pageNum = i + 1;
                                            return (
                                                <Pagination.Item
                                                    key={pageNum}
                                                    active={pageNum === pagination.page + 1}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}

                                        <Pagination.Next
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.page + 2)}
                                        />
                                        <Pagination.Last
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.totalPages)}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ArticlesDashboard;
